import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { socketClient } from '../index';

const normalizeServicesPayload = (payload) =>
  Array.isArray(payload) ? payload : payload?.services || [];

const emitScopedServicesRequest = ({
  emitEvent,
  payload,
  timeoutMessage
}) => new Promise((resolve, reject) => {
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off('getServices', handleSuccess);
    socketClient.off('getServicesError', handleError);
    resolve(normalizeServicesPayload(response));
  };

  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off('getServices', handleSuccess);
    socketClient.off('getServicesError', handleError);
    reject(new Error(response?.message || timeoutMessage));
  };

  const timeoutId = setTimeout(() => {
    socketClient.off('getServices', handleSuccess);
    socketClient.off('getServicesError', handleError);
    reject(new Error(timeoutMessage));
  }, 10000);

  socketClient.on('getServices', handleSuccess);
  socketClient.on('getServicesError', handleError);
  socketClient.emit(emitEvent, payload);
});

const initialState = {
  services: [],
  loading: false,
  error: null
};

const buildRequestId = (scope) =>
  `services:${scope}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

export const fetchServices = createAsyncThunk(
  'services/fetchServices',
  async function (_, { rejectWithValue }) {
    try {
      return await emitScopedServicesRequest({
        emitEvent: 'getServices',
        payload: {
          idToken: await firebase.auth().currentUser.getIdToken(),
          requestId: buildRequestId('list')
        },
        timeoutMessage: 'Timed out while loading services'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load services');
    }
  }
);

export const createService = createAsyncThunk(
  'services/createService',
  async function (payload, { rejectWithValue }) {
    try {
      return await emitScopedServicesRequest({
        emitEvent: 'createService',
        payload: {
          ...payload,
          idToken: await firebase.auth().currentUser.getIdToken(),
          requestId: buildRequestId('create')
        },
        timeoutMessage: 'Timed out while creating service'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to create service');
    }
  }
);

export const editService = createAsyncThunk(
  'services/editService',
  async function (payload, { rejectWithValue }) {
    try {
      return await emitScopedServicesRequest({
        emitEvent: 'editService',
        payload: {
          ...payload,
          idToken: await firebase.auth().currentUser.getIdToken(),
          requestId: buildRequestId(`edit:${payload.id}`)
        },
        timeoutMessage: 'Timed out while updating service'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to update service');
    }
  }
);

export const deleteService = createAsyncThunk(
  'services/deleteService',
  async function (id, { rejectWithValue }) {
    try {
      return await emitScopedServicesRequest({
        emitEvent: 'deleteService',
        payload: {
          id,
          idToken: await firebase.auth().currentUser.getIdToken(),
          requestId: buildRequestId(`delete:${id}`)
        },
        timeoutMessage: 'Timed out while deleting service'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to delete service');
    }
  }
);

const slice = createSlice({
  name: 'services',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.services = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.services = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(editService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editService.fulfilled, (state, action) => {
        state.services = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(editService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.services = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  }
});

export const reducer = slice.reducer;
